/**
 * loadingPhase.js — pure helpers for the search loader and the
 * placeholder → card morph (components/SearchLoadingView, MorphReveal).
 * Kept free of React so the rules are unit-testable.
 */

const SGP_TEXT_RE = /\bsgps?\b|same\s*game\s*parlay|parlays?|same\s*game/i;

/** Loader mode from the route payload and the typed query. */
export function loaderMode({ query, routePayload, isLiveQuery } = {}) {
  if (routePayload?.photoUploadInProgress) return 'photo';
  if (isLiveQuery || routePayload?.isLive) return 'live';
  // A typed "Liberty SGP" is an SGP search too, not only the SGP buttons.
  if (routePayload?.fromCachedSGP === true || routePayload?.mode === 'sgp' || SGP_TEXT_RE.test(query || '')) {
    return 'sgp';
  }
  return 'bets';
}

/** The loader's step list; loaderStepIndex maps searchStatus.step onto it. */
export const LOADER_STEPS = ['Understanding your search', 'Finding games', 'Scanning markets & prices', 'Ranking by EV'];

export function loaderStepIndex(step) {
  const s = String(step || '').toLowerCase();
  if (s === 'complete') return LOADER_STEPS.length;
  if (/processing results|ranking/.test(s)) return 3;
  if (/loading cached|scanning|sgp cache/.test(s)) return 2;
  if (/processing query|finding|fixture/.test(s)) return 1;
  return 0;
}

/** "Scanned 23 markets · 1,839 candidate bets", or '' when the backend didn't say. */
export function formatScanSummary({ marketsScanned, candidates } = {}) {
  const parts = [];
  if (Number.isFinite(marketsScanned) && marketsScanned > 0) {
    parts.push(`${marketsScanned.toLocaleString('en-US')} ${marketsScanned === 1 ? 'market' : 'markets'}`);
  }
  if (Number.isFinite(candidates) && candidates > 0) {
    parts.push(`${candidates.toLocaleString('en-US')} candidate ${candidates === 1 ? 'bet' : 'bets'}`);
  }
  return parts.length ? `Scanned ${parts.join(' · ')}` : '';
}

export function loaderTitle(mode) {
  switch (mode) {
    case 'photo': return 'Reading your photo';
    case 'live': return 'Searching live odds';
    case 'sgp': return 'Building same-game parlays';
    default: return 'Searching for bets';
  }
}

function fixtureKey(option) {
  const raw = option?.raw || {};
  const id = option?._fixtureId ?? option?.fixture_id ?? raw.fixture_id ?? raw.fixtureId ?? option?.fixtureId;
  if (id !== undefined && id !== null && id !== '') return String(id);
  const home = option?.homeTeam ?? raw.home_team ?? raw.homeTeam;
  const away = option?.awayTeam ?? raw.away_team ?? raw.awayTeam;
  return home || away ? `${away || ''}@${home || ''}` : null;
}

/** Bet and distinct-game counts for the "Found N bets · M games" moment. */
export function summarizeResults(options) {
  const list = Array.isArray(options) ? options.filter(Boolean) : [];
  const games = new Set();
  for (const option of list) {
    const key = fixtureKey(option);
    if (key) games.add(key);
  }
  return { bets: list.length, games: games.size };
}

export function formatFoundSummary({ bets, games } = {}, mode = 'bets') {
  if (!Number.isFinite(bets)) return '';
  if (bets === 0) return 'No matches found';
  // "lines", the unit the results header uses ("271 lines in 6 cards · 74 hidden"):
  // the loader counts before filters, so the page's shown + hidden add up to it.
  const noun = mode === 'sgp' ? (bets === 1 ? 'parlay' : 'parlays') : (bets === 1 ? 'line' : 'lines');
  const gamePart = games > 0 ? ` · ${games} ${games === 1 ? 'game' : 'games'}` : '';
  return `Found ${bets.toLocaleString('en-US')} ${noun}${gamePart}`;
}

/** "4.2s" under 60s, "1m 05s" after. */
export function formatElapsed(ms) {
  const safe = Math.max(0, Number(ms) || 0);
  if (safe < 60000) return `${(safe / 1000).toFixed(1)}s`;
  const totalSec = Math.floor(safe / 1000);
  const m = Math.floor(totalSec / 60);
  const s = String(totalSec % 60).padStart(2, '0');
  return `${m}m ${s}s`;
}

// Only the first screenful morphs; later cards (below the fold, next pages)
// render normally so pagination and filtering never replay the reveal.
export const REVEAL_MAX_CARDS = 8;
export const REVEAL_STAGGER_MS = 70;
export const REVEAL_BASE_DELAY_MS = 140; // lets the Suggested Legs scroll start first
export const REVEAL_DURATION_MS = 560;
// How long after loading ends newly mounted cards still count as "the reveal".
export const REVEAL_WINDOW_MS = 1200;

export function shouldMorph(index, revealActive) {
  return !!revealActive && Number.isInteger(index) && index >= 0 && index < REVEAL_MAX_CARDS;
}

export function revealDelayMs(index) {
  const i = Math.max(0, Math.min(Number(index) || 0, REVEAL_MAX_CARDS - 1));
  return REVEAL_BASE_DELAY_MS + i * REVEAL_STAGGER_MS;
}
